/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import type { Calendar } from '@services/requests/housien-models/Calendar';
import type { UserRole } from '@/shared/utils/permissions';
import { isOperationalOrHigher } from '@/shared/utils/permissions';

/**
 * Reports whether the user may create or edit events on one calendar.
 *
 * Mirrors the backend rule in CalendarEventMixin._assert_can_write_to_calendar:
 * the agency's institutional calendar is reserved for operational and above,
 * and a personal calendar belongs to its owner alone. The backend enforces
 * this on every write; filtering here keeps the UI from offering a target the
 * API would refuse.
 * @param calendar - The calendar being considered as a target.
 * @param userEmail - Email of the signed-in user.
 * @param role - Role of the signed-in user in this agency.
 * @returns True when the user may write events into the calendar.
 */
export const canWriteToCalendar = (
  calendar: Calendar,
  userEmail: string,
  role: UserRole | null | undefined,
): boolean => {
  if (calendar.owner_type === 'agency') {
    return isOperationalOrHigher(role);
  }
  return Boolean(userEmail) && calendar.owner_id === userEmail;
};

/**
 * Narrows a calendar list to the ones the user may write events into.
 * @param calendars - Calendars to filter.
 * @param userEmail - Email of the signed-in user.
 * @param role - Role of the signed-in user in this agency.
 * @returns The writable subset, in the original order.
 */
export const writableCalendars = (
  calendars: Calendar[],
  userEmail: string,
  role: UserRole | null | undefined,
): Calendar[] =>
  calendars.filter((c) => canWriteToCalendar(c, userEmail, role));
